import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeaf, faSun, faMoon, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

const CustomSunIcon = ({ className, style }) => (
  <svg 
    className={className}
    style={style}
    width="1.6rem" 
    height="1.6rem" 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    {/* Central sun circle */}
    <circle cx="12" cy="12" r="5" fill="currentColor"/>
    
    {/* Sun rays */}
    <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      {/* Vertical and horizontal rays */}
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </g>
  </svg>
);

const DonatePage = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : true;
  });
  const [particles, setParticles] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);

  // Check window width and update on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    handleResize(); // Initialize
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Generate initial particles
  const generateParticles = useCallback(() => {
    const isMobile = windowWidth <= 768;
    const newParticles = Array.from({ length: isMobile ? 25 : 50 }, (_, i) => ({
      id: i,
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      size: Math.random() * 4 + 1,
      opacity: Math.random() * 0.5 + 0.1,
      rotation: Math.random() * 360,
      speedY: -Math.random() * 0.5 - 0.1, // Added speed properties
      speedX: (Math.random() - 0.5) * 0.2
    }));
    setParticles(newParticles);
  }, [windowWidth]);

  // Update particle positions
  const updateParticles = useCallback(() => {
    setParticles(prevParticles => 
      prevParticles.map(particle => {
        let newY = particle.y + particle.speedY;
        let newX = particle.x + particle.speedX;
        
        // Reset particle position when it goes off screen
        if (newY < 0) {
          newY = window.innerHeight;
          newX = Math.random() * window.innerWidth;
        }
        
        // Wrap particles horizontally
        if (newX > window.innerWidth) newX = 0;
        if (newX < 0) newX = window.innerWidth;
  
        return {
          ...particle,
          x: newX,
          y: newY,
          rotation: (particle.rotation + 0.5) % 360
        };
      })
    );
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (windowWidth > 768) { // Only track cursor on desktop
        setMousePosition({ x: e.clientX, y: e.clientY });
        if (cursorRef.current) {
          cursorRef.current.style.left = `${e.clientX}px`;
          cursorRef.current.style.top = `${e.clientY}px`;
        }
        if (cursorDotRef.current) {
          cursorDotRef.current.style.left = `${e.clientX}px`;
          cursorDotRef.current.style.top = `${e.clientY}px`;
        }
      }
    };
  
    const handleScroll = () => {
      const elements = document.querySelectorAll('.fade-in');
      elements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        setIsVisible(prev => ({
          ...prev,
          [index]: rect.top <= window.innerHeight - 100
        }));
      });
    };
  
    // Initialize particles
    generateParticles();
  
    // Set up animation interval
    const animationInterval = setInterval(updateParticles, 16);
  
    document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    
    // Only add mousemove event listener on non-mobile devices
    if (windowWidth > 768) {
      document.addEventListener('mousemove', handleMouseMove);
    }
  
    // Add resize handler
    const handleResize = () => {
      generateParticles();
    };
    window.addEventListener('resize', handleResize);
  
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (windowWidth > 768) {
        document.removeEventListener('mousemove', handleMouseMove);
      }
      clearInterval(animationInterval);
    };
  }, [isDarkMode, generateParticles, updateParticles, windowWidth]);

  const toggleTheme = () => {
    setIsDarkMode(prevMode => {
      const newMode = !prevMode;
      localStorage.setItem('theme', newMode ? 'dark' : 'light');
      return newMode;
    });
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Updated navbar styles to match Team.js
  const styles = {
    desktopNav: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: windowWidth <= 768 ? '10px' : '20px',
      position: 'relative',
      zIndex: 100,
      transition: 'all 0.3s ease'
    },
    logoSection: {
      display: 'flex',
      alignItems: 'center',
      fontSize: windowWidth <= 480 ? '0.9rem' : 'clamp(1rem, 3vw, 1.5rem)',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: windowWidth <= 480 ? '205px' : 'none',
    },
    logoIcon: { marginRight: '8px' },
    navControls: {
      display: 'flex',
      alignItems: 'center',
      gap: windowWidth <= 480 ? '5px' : '10px',
    },
    desktopNavLinks: {
      display: windowWidth <= 768 ? 'none' : 'flex',
      gap: '20px',
    },
    navLink: {
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      textDecoration: 'none',
    },
    themeToggle: {
      background: 'none',
      border: 'none',
      fontSize: '1.2rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      cursor: 'pointer',
      padding: '5px',
      marginRight: '5px',
      transition: 'transform 0.3s ease'
    },
    hamburger: {
      display: windowWidth <= 768 ? 'block' : 'none',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      cursor: 'pointer',
      padding: '5px',
    },
    mobileMenu: {
      display: mobileMenuOpen ? 'flex' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      zIndex: 1000,
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    mobileMenuClose: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      color: isDarkMode ? '#2ecc71' : '#15523E',
      cursor: 'pointer',
    },
    mobileNavLinks: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      fontSize: '1.5rem',
    },
    mobileNavLink: {
      color: isDarkMode ? '#2ecc71' : '#15523E',
      textDecoration: 'none',
      padding: '10px',
    }
  };

  return (
    <>
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

        :root {
          --primary-green: #2ecc71;
          --dark-green: #27ae60;
          --light-green: #52c234;
          --accent-green: #32de84;
          --nav-light-green: #93D694;
          --dark-primary-green: #2ecc71;
          --light-dark-green: #15523E;
          --light-light-green: #5EAB70;
          --light-white: #f4f4f4;
        }

        body {
          font-family: 'Poppins', sans-serif;
          margin: 0;
          padding: 0;
          overflow-x: hidden;
        }

        .dark-theme {
          background-color: #000;
          color: #fff;
        }

        .light-theme {
          background-color: #fff;
          color: #15523E;
        }

        /* Custom cursor only on non-mobile */
        @media (min-width: 769px) {
          .custom-cursor {
            width: 30px;
            height: 30px;
            border: 2px solid #2ecc71;
            border-radius: 50%;
            position: fixed;
            pointer-events: none;
            z-index: 9999;
            transform: translate(-50%, -50%);
            transition: width 0.3s ease, height 0.3s ease;
          }
          
          .cursor-dot {
            width: 4px;
            height: 4px;
            background-color: #2ecc71;
            border-radius: 50%;
            position: fixed;
            pointer-events: none;
            z-index: 9999;
            transform: translate(-50%, -50%);
          }

          .dark-theme .custom-cursor {
            border-color: #2ecc71;
          }

          .dark-theme .cursor-dot {
            background-color: #2ecc71;
          }

          .light-theme .custom-cursor {
            border-color: #15523E;
          }

          .light-theme .cursor-dot {
            background-color: #15523E;
          }

          a:hover ~ .custom-cursor,
          button:hover ~ .custom-cursor,
          .initiative-card:hover ~ .custom-cursor {
            width: 50px;
            height: 50px;
          }
        }

        .donation-hero {
          height: 70vh;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          position: relative;
          overflow: hidden;
          padding-top: 90px; /* Increased padding for fixed nav */
        }

        .hero-content {
          z-index: 1;
          max-width: 800px;
          padding: 0 20px;
        }

        .hero-content h1 {
          font-size: 4.5rem;
          margin-bottom: 20px;
          background: linear-gradient(135deg, var(--primary-green), var(--light-green));
          -webkit-background-clip: text;
          background-clip: text; /* Standard property */
          -webkit-text-fill-color: transparent;
          text-fill-color: transparent; /* Standard property */
          font-weight: 700;
          letter-spacing: -1px;
        }

        .hero-content p {
          font-size: 1.2rem;
          line-height: 1.8;
          font-weight: 300;
        }

        .donation-section {
          padding: 20px 20px 100px;
          max-width: 1200px;
          margin: 0 auto;
          text-align: center;
        }

        .impact-section {
          padding: 100px 20px;
        }

        .dark-theme .impact-section {
          background: rgba(0, 0, 0, 0.9);
        }

        .light-theme .impact-section {
          background: white;
        }

        .impact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 30px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .impact-card {
          padding: 30px;
          text-align: center;
          opacity: 0;
          transform: translateY(50px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }

        .impact-card.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .impact-icon {
          font-size: 2.5rem;
          margin-bottom: 20px;
        }

        .donate-button {
          display: inline-block;
          padding: 20px 60px;
          background: var(--primary-green);
          border: none;
          border-radius: 8px;
          color: #fff;
          font-size: 1.4rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 30px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .donate-button:hover {
          background: var(--dark-green);
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(46, 204, 113, 0.2);
        }

        .particle {
          position: fixed;
          pointer-events: none;
          border-radius: 50%;
          z-index: 0;
        }

        .dark-theme .particle {
          background-color: #2ecc71;
          box-shadow: 0 0 10px rgba(46, 204, 113, 0.1);
        }

        .light-theme .particle {
          background-color: #5EAB70;
          box-shadow: 0 0 10px rgba(21, 82, 62, 0.1);
        }

        /* Mobile responsive styles */
        @media (max-width: 768px) {
          .hero-content h1 {
            font-size: 2.8rem;
          }
          
          .hero-content p {
            font-size: 1rem;
          }

          .donate-button {
            padding: 15px 40px;
            font-size: 1.2rem;
          }

          .impact-grid {
            grid-template-columns: 1fr;
          }
          
          .custom-cursor, .cursor-dot {
            display: none;
          }
          
          .logo_dark, .logo_light {
            font-size: 1rem;
          }
          
          html, body {
            touch-action: manipulation;
            overflow-x: hidden;
          }
          
          .particle {
            opacity: 0.6;
          }
        }

        /* Small mobile styles */
        @media (max-width: 480px) {
          .hero-content h1 {
            font-size: 2.2rem;
          }

          .hero-content p {
            font-size: 0.9rem;
          }

          .donate-button {
            padding: 12px 30px;
            font-size: 1rem;
          }
          
          .logo_dark, .logo_light {
            font-size: 0.9rem;
            max-width: 200px;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
        
        @media (max-width: 360px) {
          nav {
            padding: 8px !important;
          }
        }
      `}</style>

      <div className={isDarkMode ? 'dark-theme' : 'light-theme'}>
        {particles.map(particle => (
          <div
            key={particle.id}
            className="particle"
            style={{
              left: `${particle.x}px`,
              top: `${particle.y}px`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              opacity: particle.opacity,
              transform: `rotate(${particle.rotation}deg)`
            }}
          />
        ))}

        {/* Updated Navigation Bar to match Team.js */}
        <nav style={styles.desktopNav}>
          <Link to="/" style={styles.logoSection}>
            <FontAwesomeIcon icon={faLeaf} style={styles.logoIcon} />
            <span style={styles.logoText}>The Sustainable Formula</span>
          </Link>
          
          <div style={styles.navControls}>
            <div style={styles.desktopNavLinks}>
              <Link to="/team" style={styles.navLink}>Our Team</Link>
              <Link to="/about" style={styles.navLink}>About Us</Link>
              <Link to="/initiatives" style={styles.navLink}>Initiatives</Link>
              <Link to="/donate" style={styles.navLink}>Donate</Link>
            </div>
            
            <button 
              style={styles.themeToggle}
              onClick={toggleTheme}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? (
                <CustomSunIcon />
              ) : (
                <FontAwesomeIcon icon={faMoon} />
              )}
            </button>
            
            <button 
              style={styles.hamburger} 
              onClick={toggleMobileMenu}
              aria-label="Menu"
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
          </div>
        </nav>

        {/* Updated Mobile Menu to match Team.js */}
        <div style={styles.mobileMenu}>
          <button 
            style={styles.mobileMenuClose} 
            onClick={toggleMobileMenu}
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <div style={styles.mobileNavLinks}>
            <Link 
              to="/team" 
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              Our Team
            </Link>
            <Link 
              to="/about" 
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              About Us
            </Link>
            <Link 
              to="/initiatives" 
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              Initiatives
            </Link>
            <Link 
              to="/donate" 
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              Donate
            </Link>
          </div>
        </div>

        {windowWidth > 768 && (
          <>
            <div ref={cursorRef} className="custom-cursor" />
            <div ref={cursorDotRef} className="cursor-dot" />
          </>
        )}

        <section className="donation-hero">
          <div className="hero-content">
            <h1>Support Our Mission</h1>
            <p>Your contribution helps us create a more sustainable future. Every donation directly supports our initiatives, making a tangible difference in the fight for environmental conservation.</p>
          </div>
        </section>

        <section className="donation-section">
          <button 
            className="donate-button" 
            onClick={() => window.open('https://milaap.org/fundraisers/support-the-sustainable-formula', '_blank')}
          >
            Donate Now
          </button>
        </section>

        <section className="impact-section">
          <div className="impact-grid">
            <div className={`impact-card fade-in ${isVisible[0] ? 'visible' : ''}`}>
              <div className="impact-icon">🌱</div>
              <h3>Environmental Impact</h3>
              <p>Your donation directly contributes to planting trees, cleaning oceans, and preserving wildlife habitats.</p>
            </div>
            <div className={`impact-card fade-in ${isVisible[1] ? 'visible' : ''}`}>
              <div className="impact-icon">💧</div>
              <h3>Clean Water</h3>
              <p>Help us provide sustainable water solutions to communities in need around the world.</p>
            </div>
            <div className={`impact-card fade-in ${isVisible[2] ? 'visible' : ''}`}>
              <div className="impact-icon">♻️</div>
              <h3>Recycling Programs</h3>
              <p>Support innovative recycling programs that keep our planet clean and reduce waste.</p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DonatePage;